import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CalendarDays, Video, MapPin, Users, CheckCircle2, XCircle, ExternalLink, Info } from "lucide-react";
import { authHeaders, fmtDatum, formatApiErrorDetail } from "@/components/schulungen/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TermineView() {
  const { user } = useAuth();
  const [termine, setTermine] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => axios.get(`${API}/schulungen/termine`, authHeaders(user)).then((r) => setTermine(r.data)).catch(() => toast.error("Termine konnten nicht geladen werden")), [user]);
  useEffect(() => { document.title = "Online-Präsenztermine | Lernbereich"; load(); }, [load]);

  const buchen = async (t) => {
    setBusy(t.id);
    try { await axios.post(`${API}/schulungen/termine/${t.id}/buchen`, {}, authHeaders(user)); toast.success("Termin gebucht"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(null);
  };
  const storno = async (t) => {
    if (!window.confirm("Buchung wirklich stornieren?")) return;
    setBusy(t.id);
    try { await axios.delete(`${API}/schulungen/termine/${t.id}/buchen`, authHeaders(user)); toast.success("Buchung storniert"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(null);
  };

  if (!termine) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;
  const gebucht = termine.find((t) => t.gebucht);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="termine-view">
      <div>
        <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900">Online-Präsenztermine</h1>
        <p className="text-slate-500 mt-1">Der Praxisteil (20 UStd) ist ein freiwilliges Angebot und findet <strong className="text-slate-700">online als Video-Seminar</strong> statt – bequem von zu Hause oder vom Arbeitsplatz aus. Sie können einen Termin wählen.</p>
      </div>
      <div className="flex gap-3 p-4 rounded-xl bg-cyan-50 border border-cyan-100 text-sm text-cyan-900" data-testid="termine-hinweis"><Info className="h-5 w-5 shrink-0" />Für das Zertifikat ist der bestandene Abschlusstest entscheidend. Der Online-Präsenzteil vertieft die Inhalte praktisch: Rollenspiele, Empathietraining, Notfallmanagement und Fallbesprechung – live per Video.</div>

      {gebucht && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5" data-testid="gebuchter-termin">
          <p className="text-xs font-bold tracking-wider uppercase text-green-700 mb-1">Ihr gebuchter Termin</p>
          <p className="font-semibold text-slate-800">{fmtDatum(gebucht.datum)}, {gebucht.uhrzeit_von}–{gebucht.uhrzeit_bis} Uhr · {gebucht.art === "video" ? "Video-Seminar" : gebucht.ort}</p>
          {gebucht.art === "video" && gebucht.video_room && (
            <a href={`/video/${gebucht.video_room}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-semibold" data-testid="video-join-link"><Video className="h-4 w-4" />Zum Video-Raum<ExternalLink className="h-3.5 w-3.5" /></a>
          )}
        </div>
      )}

      <div className="space-y-3">
        {termine.length === 0 && <div className="bg-white border border-slate-200/60 rounded-2xl p-10 text-center text-slate-500" data-testid="termine-empty"><CalendarDays className="h-10 w-10 mx-auto mb-3 text-slate-300" />Aktuell sind keine Termine ausgeschrieben. Die Kursleitung informiert Sie, sobald neue Termine verfügbar sind.</div>}
        {termine.map((t) => (
          <div key={t.id} className={`bg-white border rounded-2xl p-5 flex flex-wrap items-center gap-4 ${t.gebucht ? "border-green-300" : "border-slate-200/60"}`} data-testid={`termin-${t.id}`}>
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${t.art === "video" ? "bg-cyan-50 text-cyan-700" : "bg-amber-50 text-amber-700"}`}>{t.art === "video" ? <Video className="h-6 w-6" /> : <MapPin className="h-6 w-6" />}</div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-slate-800">{fmtDatum(t.datum)} · {t.uhrzeit_von}–{t.uhrzeit_bis} Uhr</p>
              <p className="text-sm text-slate-500">{t.art === "video" ? "Video-Seminar (online)" : t.ort}{t.beschreibung && ` · ${t.beschreibung}`}</p>
              <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t.plaetze_frei} von {t.max_plaetze} Plätzen frei</p>
            </div>
            {t.gebucht ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700"><CheckCircle2 className="h-5 w-5" />Gebucht</span>
                <button onClick={() => storno(t)} disabled={busy === t.id} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600" data-testid={`termin-storno-${t.id}`}><XCircle className="h-4 w-4" />Stornieren</button>
              </div>
            ) : (
              <Button onClick={() => buchen(t)} disabled={busy === t.id || !!gebucht || t.plaetze_frei < 1} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-11 px-5 font-semibold" data-testid={`termin-buchen-${t.id}`}>{t.plaetze_frei < 1 ? "Ausgebucht" : "Buchen"}</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
